import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { calendarAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Modal } from '../UI';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  parseISO,
} from 'date-fns';
import {
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineExternalLink,
  HiOutlinePhone,
  HiOutlineClipboardList,
  HiOutlineArrowLeft,
  HiOutlineUser,
} from 'react-icons/hi';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EVENT_STYLES = {
  followup: {
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
    dot: 'bg-indigo-500',
    icon: HiOutlinePhone,
    label: 'Follow-up',
  },
  task: {
    chip: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
    dot: 'bg-amber-500',
    icon: HiOutlineClipboardList,
    label: 'Task',
  },
};

// Minutes since midnight, for sorting a day's events; untimed events go last
const eventMinutes = (ev) => {
  if (!ev.time) return 24 * 60;
  if (ev.time.includes('T')) {
    const d = new Date(ev.time);
    return isNaN(d) ? 24 * 60 : d.getHours() * 60 + d.getMinutes();
  }
  const m = /^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?/.exec(ev.time);
  if (!m) return 24 * 60;
  let h = Number(m[1]);
  const ampm = m[3]?.toLowerCase();
  if (ampm) h = (h % 12) + (ampm === 'pm' ? 12 : 0);
  return h * 60 + Number(m[2]);
};

const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

/**
 * Follow-ups calendar view
 *
 * Displays scheduled follow-ups and activities in a month calendar.
 * When the organization has Google Calendar integrated (Settings → Calendar),
 * each event gets an "Add to Google Calendar" action; if it is not
 * integrated, that part is skipped entirely.
 */
const FollowUpsCalendar = () => {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(null);
  // Day agenda: 'yyyy-MM-dd' of the day whose full event list is open
  const [dayKey, setDayKey] = useState(null);
  const navigate = useNavigate();

  // 6-week grid covering the visible month
  const grid = useMemo(
    () => ({
      start: startOfWeek(startOfMonth(cursor)),
      end: endOfWeek(endOfMonth(cursor)),
    }),
    [cursor],
  );

  const range = useMemo(
    () => ({
      start: format(grid.start, 'yyyy-MM-dd'),
      end: format(grid.end, 'yyyy-MM-dd'),
    }),
    [grid],
  );

  const eventsQuery = useQuery({
    queryKey: ['calendar-events', range.start, range.end],
    queryFn: async () => {
      const response = await calendarAPI.getEvents(range);
      return response?.data?.data || [];
    },
    refetchOnWindowFocus: false,
  });

  // Google Calendar integration check — "Add to Google Calendar" is only
  // offered when the org saved its Google Calendar ID in Settings → Calendar.
  const settingsQuery = useQuery({
    queryKey: ['calendar-settings', 'followups-calendar'],
    queryFn: async () => {
      const response = await calendarAPI.getSettings();
      return response?.data?.data || {};
    },
    refetchOnWindowFocus: false,
  });
  const googleIntegrated = Boolean(settingsQuery.data?.googleCalendarId);

  const events = eventsQuery.data || [];
  const byDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      if (!ev.date) continue;
      const key = format(new Date(ev.date), 'yyyy-MM-dd');
      (map[key] = map[key] || []).push(ev);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => eventMinutes(a) - eventMinutes(b));
    }
    return map;
  }, [events]);

  const days = useMemo(
    () => eachDayOfInterval({ start: grid.start, end: grid.end }),
    [grid],
  );

  const eventTime = (ev) => {
    if (!ev.time) return null;
    try {
      return ev.time.includes('T')
        ? format(new Date(ev.time), 'hh:mm a')
        : ev.time;
    } catch {
      return ev.time;
    }
  };

  const buildGoogleUrl = (ev) => {
    let start = ev.date ? parseISO(`${format(new Date(ev.date), 'yyyy-MM-dd')}T09:00:00`) : new Date();
    const time = ev.time ? /^(\d{1,2}):(\d{2})/.exec(ev.time) : null;
    if (time) start.setHours(Number(time[1]), Number(time[2]), 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const stamp = (d) => format(d, "yyyyMMdd'T'HHmmss");
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: ev.title || 'Follow-up',
      dates: `${stamp(start)}/${stamp(end)}`,
      details: `${EVENT_STYLES[ev.type]?.label || 'Event'} scheduled via TeleValley CRM${
        ev.assigneeName ? ` — assigned to ${ev.assigneeName}` : ''
      }`,
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const addToGoogle = (ev) => {
    try {
      window.open(buildGoogleUrl(ev), '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Could not open Google Calendar');
    }
  };

  const eventDate = selected?.date ? new Date(selected.date) : null;
  const dayEventsOpen = dayKey ? byDay[dayKey] || [] : [];
  const dayFollowUps = dayEventsOpen.filter((e) => e.type !== 'task').length;
  const dayTasks = dayEventsOpen.length - dayFollowUps;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  const closeModal = () => {
    setSelected(null);
    setDayKey(null);
  };

  return (
    <div className="space-y-4">
      {/* Month navigation */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCursor((c) => subMonths(c, 1))}
            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            aria-label="Previous month"
          >
            <HiOutlineChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCursor(startOfMonth(new Date()))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
          >
            Today
          </button>
          <button
            onClick={() => setCursor((c) => addMonths(c, 1))}
            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            aria-label="Next month"
          >
            <HiOutlineChevronRight className="w-4 h-4" />
          </button>
        </div>
        <h2 className="text-base sm:text-lg font-bold text-gray-900">
          {format(cursor, 'MMMM yyyy')}
        </h2>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-indigo-500" /> Follow-ups
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500" /> Tasks
          </span>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {eventsQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide"
                >
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {days.map((day) => {
                const key = format(day, 'yyyy-MM-dd');
                const dayEvents = byDay[key] || [];
                const outside = !isSameMonth(day, cursor);
                return (
                  <div
                    key={key}
                    className={`min-h-[92px] sm:min-h-[110px] border-b border-r border-gray-100 p-1.5 ${
                      outside ? 'bg-gray-50/60' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <button
                        type="button"
                        onClick={() => setDayKey(key)}
                        disabled={!dayEvents.length}
                        aria-label={`${format(day, 'dd MMM yyyy')}, ${plural(dayEvents.length, 'event')}`}
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                          isToday(day)
                            ? 'bg-primary-600 text-white'
                            : outside
                              ? 'text-gray-400'
                              : 'text-gray-700'
                        } ${dayEvents.length ? 'hover:ring-2 hover:ring-primary-200' : 'cursor-default'}`}
                      >
                        {format(day, 'd')}
                      </button>
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((ev) => {
                        const style = EVENT_STYLES[ev.type] || EVENT_STYLES.followup;
                        const time = eventTime(ev);
                        return (
                          <button
                            key={`${ev.type}-${ev.id}`}
                            onClick={() => setSelected(ev)}
                            className={`w-full text-left px-1.5 py-1 rounded border text-[11px] leading-tight truncate flex items-center gap-1 ${style.chip}`}
                            title={ev.title}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
                            <span className="truncate">
                              {time ? `${time} ` : ''}{ev.title}
                            </span>
                          </button>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <button
                          type="button"
                          onClick={() => setDayKey(key)}
                          className="w-full text-left text-[11px] font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded px-1.5 py-0.5"
                        >
                          +{dayEvents.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Event detail modal */}
      <Modal
        isOpen={Boolean(selected || dayKey)}
        onClose={closeModal}
        title={
          selected
            ? selected.type === 'task' ? 'Task Details' : 'Follow-up Details'
            : dayKey
              ? format(parseISO(dayKey), 'EEEE, dd MMM yyyy')
              : ''
        }
      >
        {/* Day agenda — every event on the day, not just the 3 shown in the cell */}
        {!selected && dayKey && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              {plural(dayEventsOpen.length, 'event')} · {plural(dayFollowUps, 'follow-up')},{' '}
              {plural(dayTasks, 'task')}
            </p>
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg max-h-[60vh] overflow-y-auto">
              {dayEventsOpen.map((ev) => {
                const style = EVENT_STYLES[ev.type] || EVENT_STYLES.followup;
                const Icon = style.icon;
                const time = eventTime(ev);
                return (
                  <li key={`${ev.type}-${ev.id}`}>
                    <button
                      type="button"
                      onClick={() => setSelected(ev)}
                      className="w-full flex items-center gap-2 sm:gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="w-11 sm:w-16 flex-shrink-0 text-[11px] sm:text-xs font-medium text-gray-500 leading-tight">
                        {time || 'Any time'}
                      </span>
                      <span className={`w-7 h-7 flex-shrink-0 rounded-full border flex items-center justify-center ${style.chip}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-gray-900 truncate">{ev.title}</span>
                        <span className="flex items-center gap-1 text-xs text-gray-500 truncate">
                          <HiOutlineUser className="w-3 h-3 flex-shrink-0" />
                          {ev.assigneeName || 'Unassigned'}
                        </span>
                      </span>
                      <span
                        className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${
                          STATUS_BADGE[ev.status] || STATUS_BADGE.pending
                        }`}
                      >
                        {ev.status}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {selected && (
          <div className="space-y-4">
            {dayKey && (
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                <HiOutlineArrowLeft className="w-4 h-4" />
                All events on {format(parseISO(dayKey), 'dd MMM')}
              </button>
            )}
            <div>
              <h3 className="text-base font-bold text-gray-900 break-words">
                {selected.title}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {eventDate ? format(eventDate, 'EEEE, dd MMM yyyy') : '—'}
                {eventTime(selected) ? ` · ${eventTime(selected)}` : ''}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500">Type</p>
                <p className="text-gray-900 font-medium capitalize">{selected.type}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    STATUS_BADGE[selected.status] || STATUS_BADGE.pending
                  }`}
                >
                  {selected.status}
                </span>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-500">Assigned to</p>
                <p className="text-gray-900 font-medium">
                  {selected.assigneeName || 'Unassigned'}
                </p>
              </div>
            </div>

            {selected.type !== 'task' && selected.leadId && (
              <button
                type="button"
                onClick={() => navigate(`/app/leads/${selected.leadId}`)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                <HiOutlineUser className="w-4 h-4" />
                Open lead
              </button>
            )}

            {/* Attach to Google Calendar — only when integrated, skipped otherwise */}
            {googleIntegrated && (
              <button
                onClick={() => addToGoogle(selected)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <HiOutlineExternalLink className="w-4 h-4" />
                Add to Google Calendar
              </button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FollowUpsCalendar;